import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, TrendingUp, AlertCircle } from 'lucide-react';

const ROICalculator = () => {
  const [domain, setDomain] = useState('llm');
  const [traffic, setTraffic] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [result, setResult] = useState(null);

  const calculateROI = () => {
    const trafficNum = parseFloat(traffic);
    const costNum = parseFloat(unitCost);

    if (!trafficNum || !costNum) {
      return;
    }

    let monthlyCost, expectedReductionRange, unit;

    if (domain === 'llm') {
      // Traffic in millions of tokens
      monthlyCost = (trafficNum * costNum);
      expectedReductionRange = [0.35, 0.60]; // 35-60% cost reduction (target)
      unit = 'M tokens';
    } else {
      // Traffic in TB
      monthlyCost = trafficNum * costNum;
      expectedReductionRange = [0.40, 0.70]; // 40-70% egress reduction (target)
      unit = 'TB';
    }

    const savingsLow = monthlyCost * expectedReductionRange[0];
    const savingsHigh = monthlyCost * expectedReductionRange[1];
    const annualSavingsLow = savingsLow * 12;
    const annualSavingsHigh = savingsHigh * 12;

    setResult({
      monthlyCost,
      savingsLow,
      savingsHigh,
      annualSavingsLow,
      annualSavingsHigh,
      reductionRange: expectedReductionRange,
      unit
    });

    // Track analytics event
    if (window.gtag) {
      window.gtag('event', 'roi_calculated', {
        domain,
        traffic: trafficNum,
        unit_cost: costNum,
        monthly_savings_low: savingsLow,
        monthly_savings_high: savingsHigh
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Card className="max-w-4xl mx-auto bg-slate-800/80 border-cyan-400/30 backdrop-blur-sm">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Calculator className="text-white" size={24} />
                </div>
                <CardTitle className="text-3xl text-white">ROI Calculator</CardTitle>
              </div>
              <CardDescription className="text-gray-300 text-lg">
                Estimate potential cost savings with MSML technology
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <Label htmlFor="domain" className="text-white">Target Domain</Label>
                  <Select value={domain} onValueChange={setDomain}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="llm">LLM Inference</SelectItem>
                      <SelectItem value="media">Media Streaming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="traffic" className="text-white">
                    {domain === 'llm' ? 'Monthly Traffic (M tokens)' : 'Monthly Traffic (TB)'}
                  </Label>
                  <Input
                    id="traffic"
                    type="number"
                    placeholder={domain === 'llm' ? 'e.g., 1000' : 'e.g., 500'}
                    value={traffic}
                    onChange={(e) => setTraffic(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unitCost" className="text-white">
                    {domain === 'llm' ? 'Cost per M tokens ($)' : 'Cost per TB ($)'}
                  </Label>
                  <Input
                    id="unitCost"
                    type="number"
                    placeholder={domain === 'llm' ? 'e.g., 10' : 'e.g., 50'}
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={calculateROI}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                  >
                    Calculate Savings
                  </Button>
                </div>
              </div>

              {result && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg p-6 border border-green-400/30">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="text-green-400" size={24} />
                      <h4 className="text-xl font-semibold text-white">Estimated Savings (Target)</h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-gray-400 text-sm mb-2">Current Monthly Cost</p>
                        <p className="text-2xl font-bold text-white">{formatCurrency(result.monthlyCost)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-2">Potential Monthly Savings</p>
                        <p className="text-2xl font-bold text-green-400">
                          {formatCurrency(result.savingsLow)} – {formatCurrency(result.savingsHigh)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          ({(result.reductionRange[0] * 100).toFixed(0)}%–{(result.reductionRange[1] * 100).toFixed(0)}% reduction target)
                        </p>
                      </div>
                      <div className="md:col-span-2 pt-4 border-t border-gray-700">
                        <p className="text-gray-400 text-sm mb-2">Potential Annual Savings</p>
                        <p className="text-3xl font-bold text-green-400">
                          {formatCurrency(result.annualSavingsLow)} – {formatCurrency(result.annualSavingsHigh)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-400/30">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-blue-300 font-semibold mb-1">Important Note</p>
                        <p className="text-gray-300 text-sm">
                          These estimates reflect targets pending validation; actual results vary by workload and tolerance. 
                          Pricing designed for &lt;90-day payback at mid-range savings. 
                          Contact us for detailed analysis specific to your use case.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center pt-4">
                    <Button
                      onClick={() => window.location.href = '#contact'}
                      variant="outline"
                      className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900"
                    >
                      Discuss Your Use Case
                    </Button>
                  </div>
                </motion.div>
              )}

              {!result && (
                <div className="text-center py-8 text-gray-400">
                  <Calculator className="mx-auto mb-3 opacity-50" size={48} />
                  <p>Enter your parameters above to calculate potential savings</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ROICalculator;
